/**
 * Benefits Tab - Business Value Calculator
 * Sprint 7: Benefits Tab Redesign COMPLETE
 *
 * Features:
 * - Sticky benefits summary banner with 4 key metrics
 * - Level 0/1 accordion hierarchy (4 pillars → 12 drivers)
 * - Formula-based calculators (not simple multiplication)
 * - Journey recommendation integration (pre-fill parameters)
 * - Real-time auto-calculation on input changes
 * - Risk adjustment (confidence × attribution)
 * - Inline editing (no modal calculators)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Accordion, AccordionSection } from './Accordion';
import { BenefitsSummaryBanner } from './BenefitsSummaryBanner';
import { BenefitDriverSection } from './BenefitDriverSection';
import {
  BENEFIT_CALCULATORS,
  LEVEL_0_PILLARS,
  getCalculatorForDriver,
  CalculatorParameters,
  CalculationResult
} from './benefitCalculators';

// ============================================================================
// TYPES
// ============================================================================

interface DriverBenefitAggregation {
  driver_id: string;
  driver_label: string;
  level_0_parent: string;
  total_recommendations: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  estimated_time_saved_hours: number;
  estimated_cost_reduction_usd: number;
  estimated_quality_improvement_pct: number;
  estimated_risk_reduction_pct: number;
  recommendation_titles: string[];
  recommendation_ids: string[];
}

interface CalculatorImportData {
  journey_id: string;
  journey_name: string;
  export_date: string;
  driver_benefits: DriverBenefitAggregation[];
  // V2 optional fields
  estimated_implementation_costs?: unknown;
  financial_parameters?: unknown;
}

interface DriverState {
  enabled: boolean;
  parameters: CalculatorParameters;
  results: CalculationResult | null;
  confidence: 'high' | 'medium' | 'low';
  attribution: number; // 0-100
  recommendationCount: number;
}

interface BenefitsTabProps {
  data: CalculatorImportData | null;
  onBenefitsUpdate: (totalBenefits: number) => void;
  onDriverUpdate?: (driverId: string, updates: Record<string, unknown>) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const BenefitsTab: React.FC<BenefitsTabProps> = ({ data, onBenefitsUpdate }) => {
  // State management - Driver states (one per Level 1 driver)
  const [driverStates, setDriverStates] = useState<Record<string, DriverState>>({});

  // State management - Open accordion section
  const [openPillarId, setOpenPillarId] = useState<string | null>('Trusted_data');

  // ==================== INITIALIZATION ====================

  // Initialize driver states from journey data
  useEffect(() => {
    if (!data || !data.driver_benefits) return;

    const initialStates: Record<string, DriverState> = {};

    // Initialize ALL 12 drivers (even if not in journey data)
    Object.keys(BENEFIT_CALCULATORS).forEach((driverId) => {
      const calculator = BENEFIT_CALCULATORS[driverId];
      const journeyDriver = data.driver_benefits.find((d) => d.driver_id === driverId);

      const hasRecommendations = !!(journeyDriver && journeyDriver.total_recommendations > 0);

      // Pre-fill parameters from journey estimates
      const preFilledParams = hasRecommendations
        ? mapJourneyDataToParams(driverId, journeyDriver)
        : calculator.defaultParameters;

      initialStates[driverId] = {
        enabled: hasRecommendations, // Auto-enable if has recommendations
        parameters: preFilledParams,
        results: null, // User must click Calculate
        confidence: hasRecommendations ? inferConfidence(journeyDriver) : 'medium',
        attribution: hasRecommendations ? inferAttribution(journeyDriver) : 80,
        recommendationCount: journeyDriver?.total_recommendations || 0
      };
    });

    setDriverStates(initialStates);

    // Auto-expand first pillar with recommendations
    const firstPillarWithRecs = Object.values(LEVEL_0_PILLARS).find((pillar) =>
      pillar.driverIds.some((dId) => initialStates[dId]?.recommendationCount > 0)
    );
    if (firstPillarWithRecs) {
      setOpenPillarId(firstPillarWithRecs.id);
    }
  }, [data]);

  // ==================== JOURNEY PRE-POPULATION LOGIC ====================

  /**
   * Map journey data to calculator parameters
   * Uses estimated_time_saved_hours and estimated_cost_reduction_usd as hints
   */
  function mapJourneyDataToParams(
    driverId: string,
    journeyData: DriverBenefitAggregation
  ): CalculatorParameters {
    const calculator = BENEFIT_CALCULATORS[driverId];
    const defaults = calculator.defaultParameters;

    // Extract journey estimates
    // Note: estimated_time_saved_hours and estimated_cost_reduction_usd available via journeyData
    const recCount = journeyData.total_recommendations || 0;

    // Driver-specific mapping logic
    switch (driverId) {
      case 'AI_Ready_Data':
        return {
          ...defaults,
          workflows_per_year: Math.max(recCount * 100, defaults.workflows_per_year || 1200)
        };

      case 'Data_quality':
        return {
          ...defaults,
          data_errors_per_year: Math.max(recCount * 50, defaults.data_errors_per_year || 500)
        };

      case 'Time_to_product':
        return {
          ...defaults,
          products_per_year: Math.max(recCount * 2, defaults.products_per_year || 10)
        };

      case 'Faster_decisions':
        return {
          ...defaults,
          decisions_per_year: Math.max(recCount * 20, defaults.decisions_per_year || 200)
        };

      case 'Resource_Savings':
        return {
          ...defaults,
          datasets_per_year: Math.max(recCount * 10, defaults.datasets_per_year || 500)
        };

      case 'Resource_Productivity':
        return {
          ...defaults,
          analysts: Math.max(Math.floor(recCount / 5), defaults.analysts || 10)
        };

      default:
        return defaults;
    }
  }

  /**
   * Infer confidence level from recommendation severity distribution
   */
  function inferConfidence(journeyData: DriverBenefitAggregation): 'high' | 'medium' | 'low' {
    const total = journeyData.total_recommendations;
    if (total === 0) return 'low';

    const criticalPct = (journeyData.critical_count / total) * 100;
    const highPct = (journeyData.high_count / total) * 100;

    if (criticalPct + highPct >= 50) return 'high';
    if (criticalPct + highPct >= 25) return 'medium';
    return 'low';
  }

  /**
   * Infer attribution percentage from recommendation count
   */
  function inferAttribution(journeyData: DriverBenefitAggregation): number {
    const recCount = journeyData.total_recommendations;

    // More recommendations = higher attribution (capped at 95%)
    if (recCount >= 10) return 95;
    if (recCount >= 5) return 85;
    if (recCount >= 3) return 75;
    return 60;
  }

  // ==================== EVENT HANDLERS ====================

  /**
   * Handle driver state update (from BenefitDriverSection)
   */
  const handleDriverUpdate = (driverId: string, updates: Partial<DriverState>) => {
    setDriverStates((prev) => ({
      ...prev,
      [driverId]: {
        ...prev[driverId],
        ...updates
      }
    }));
  };

  // ==================== CALCULATIONS ====================

  /**
   * Calculate total benefits (sum across all enabled drivers)
   */
  const summaryMetrics = useMemo(() => {
    let totalBenefits = 0;
    let totalRiskAdjusted = 0;
    let threeYearProjection = 0;
    let enabledDriverCount = 0;

    Object.values(driverStates).forEach((driver) => {
      if (!driver.enabled || !driver.results) return;

      enabledDriverCount++;

      const baseValue = driver.results.year1;
      const confidenceMultiplier = { high: 1.0, medium: 0.8, low: 0.5 }[driver.confidence];
      const riskAdjusted = baseValue * confidenceMultiplier * (driver.attribution / 100);

      totalBenefits += baseValue;
      totalRiskAdjusted += riskAdjusted;
      threeYearProjection += driver.results.total;
    });

    return {
      totalBenefits: Math.round(totalBenefits),
      totalRiskAdjusted: Math.round(totalRiskAdjusted),
      threeYearProjection: Math.round(threeYearProjection),
      enabledDriverCount,
      totalDriverCount: Object.keys(BENEFIT_CALCULATORS).length
    };
  }, [driverStates]);

  // Auto-notify parent when total benefits change
  useEffect(() => {
    if (onBenefitsUpdate) {
      onBenefitsUpdate(summaryMetrics.totalRiskAdjusted);
    }
  }, [summaryMetrics.totalRiskAdjusted, onBenefitsUpdate]);

  // ==================== ACCORDION STRUCTURE ====================

  /**
   * Build accordion sections (Level 0 pillars)
   */
  const accordionSections: AccordionSection[] = useMemo(() => {
    return Object.values(LEVEL_0_PILLARS).map((pillar) => {
      // Calculate pillar subtotal
      const pillarTotal = pillar.driverIds.reduce((sum, driverId) => {
        const driver = driverStates[driverId];
        if (!driver?.enabled || !driver.results) return sum;
        return sum + driver.results.year1;
      }, 0);

      return {
        id: pillar.id,
        title: pillar.label,
        subtitle: pillar.description,
        icon: <span style={{ fontSize: '1.5rem' }}>{pillar.icon}</span>,
        value: pillarTotal,
        children: (
          <div className="benefit-pillar-drivers">
            {pillar.driverIds.map((driverId) => {
              const calculator = getCalculatorForDriver(driverId);
              const driverState = driverStates[driverId];

              if (!calculator || !driverState) return null;

              return (
                <BenefitDriverSection
                  key={driverId}
                  calculator={calculator}
                  state={driverState}
                  recommendationCount={driverState.recommendationCount}
                  onChange={(updates) => handleDriverUpdate(driverId, updates)}
                />
              );
            })}
          </div>
        )
      };
    });
  }, [driverStates]);

  // ==================== RENDER ====================

  if (!data) {
    return (
      <div className="benefits-tab">
        <p className="no-data-message">Loading calculator data...</p>
      </div>
    );
  }

  return (
    <div className="benefits-tab">
      {/* Summary Banner (Sticky) */}
      <BenefitsSummaryBanner
        totalBenefits={summaryMetrics.totalBenefits}
        totalRiskAdjusted={summaryMetrics.totalRiskAdjusted}
        enabledDriverCount={summaryMetrics.enabledDriverCount}
        totalDriverCount={summaryMetrics.totalDriverCount}
        threeYearProjection={summaryMetrics.threeYearProjection}
      />

      {/* Level 0 Accordion (4 Pillars) */}
      <Accordion
        sections={accordionSections}
        defaultOpenId={openPillarId || 'Trusted_data'}
        onSectionChange={(sectionId) => setOpenPillarId(sectionId)}
      />
    </div>
  );
};

export default BenefitsTab;
